import * as React from 'react'
import { AlertCircle as WarningCircleIcon, MapPin as MapPinAreaIcon } from 'lucide-react'
import {
  useGetPlantAreasQuery,
  usePlantSessionPaint,
  type PlantAreaIndex,
  type PlantAreaSummary,
  type PlantSite
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { areaKindIcon, areaKindLabel, temperatureClassLabel } from './area-meta'
import { PlantHref } from './PlantHref'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionRollupPill,
  collectUnplacedGhosts,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

/**
 * `/plant/areas` — Areas tab landing.
 *
 * Areas are spatial regions (`production_zone`, `cooler`, `freezer`,
 * `utility_room`, `mezzanine`, …) anchored to a `Site` via
 * `Area -[:PART_OF]-> Site` and nesting via `Area -[:PART_OF]-> Area`.
 * Equipment, Parts, and Processes are placed inside an area via
 * `LOCATED_IN`.
 *
 * The landing renders one section per owning site (top-level areas
 * only — sub-areas are reachable from each area's detail page) plus
 * a "Strays" section for areas that aren't anchored to any site.
 * Each tile carries pre-computed subtree counts so the user can scan
 * at a glance which areas hold real assets.
 *
 * When a session is painting, tiles carry marks / rollup pills and
 * deleted areas render as ghost tiles under their site (or Strays
 * when unplaced).
 */
const AreasPage = (): React.ReactElement => {
  const { data, isLoading, isError } = useGetPlantAreasQuery()
  const index = data ?? EMPTY_INDEX
  const totalCount = React.useMemo(() => totalAreaCount(index), [index])
  const { active, filter, markById, ghostsByContainerId, markFor } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const strayGhosts = React.useMemo((): PlantSessionGhostView[] => {
    if (!paintOn) return []
    const liveIds = new Set<string>()
    for (const group of index.groups) {
      for (const area of group.areas) liveIds.add(area.id)
    }
    for (const area of index.strays) liveIds.add(area.id)
    return collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
      label: 'Area',
      liveIds
    })
  }, [ghostsByContainerId, index, markById, markFor, paintOn])

  const showStrays = index.strays.length > 0 || strayGhosts.length > 0

  return (
    <div className="flex flex-1 flex-col gap-card">
      <header>
        <h1 className="m-0 text-2xl font-semibold text-ink">Areas</h1>
        <p className="mt-1 text-sm text-ink-3">{areaSubtitle(index, totalCount)}</p>
      </header>

      {isError ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="Couldn't load areas"
            hint="Check the proxy is reachable and try again."
            icon={WarningCircleIcon}
          />
        </div>
      ) : isLoading && totalCount === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <div className="text-sm text-ink-3">Loading areas…</div>
        </div>
      ) : totalCount === 0 && !showStrays ? (
        <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
          <Empty
            title="No areas yet"
            hint="Ingest a plant layout to populate the Areas tab."
            icon={MapPinAreaIcon}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-card">
          {index.groups.map((group) => (
            <SiteGroupSection key={group.site.id} site={group.site} areas={group.areas} />
          ))}
          {showStrays && <StrayAreasSection areas={index.strays} ghosts={strayGhosts} />}
        </div>
      )}
    </div>
  )
}

function SiteGroupSection({
  site,
  areas
}: {
  site: PlantSite
  areas: PlantAreaSummary[]
}): React.ReactElement {
  const areaGhosts = useVisibleGhosts(site.id, 'Area')
  const liveCount = areas.length
  const ghostCount = areaGhosts.length
  const subtitleCount = liveCount + ghostCount

  return (
    <section>
      <SectionHeader
        label={
          <PlantHref
            id={site.id}
            label="Site"
            node={site}
            className="hover:text-ink hover:underline"
          >
            {site.name}
          </PlantHref>
        }
        subtitle={`${subtitleCount} ${subtitleCount === 1 ? 'area' : 'areas'}`}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
        {areas.map((area) => (
          <AreaTile key={area.id} area={area} />
        ))}
        {areaGhosts.map((ghost) => (
          <SessionGhostTile
            key={ghost.id}
            name={ghost.name ?? ghost.id}
            view={ghost.view}
            icon={
              <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-st-down-bg text-st-down">
                <MapPinAreaIcon size={20} />
              </span>
            }
          />
        ))}
      </div>
    </section>
  )
}

function StrayAreasSection({
  areas,
  ghosts
}: {
  areas: PlantAreaSummary[]
  ghosts: PlantSessionGhostView[]
}): React.ReactElement {
  const total = areas.length + ghosts.length
  return (
    <section>
      <SectionHeader
        label="Strays"
        subtitle={`${total} ${total === 1 ? 'area' : 'areas'} with no site`}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(17.5rem,1fr))]">
        {areas.map((area) => (
          <AreaTile key={area.id} area={area} />
        ))}
        {ghosts.map((ghost) => (
          <SessionGhostTile
            key={ghost.id}
            name={ghost.name ?? ghost.id}
            view={ghost.view}
            icon={
              <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-st-down-bg text-st-down">
                <MapPinAreaIcon size={20} />
              </span>
            }
          />
        ))}
      </div>
    </section>
  )
}

function AreaTile({ area }: { area: PlantAreaSummary }): React.ReactElement {
  const Icon = areaKindIcon(area.area_kind)
  const kindLabel = areaKindLabel(area.area_kind)
  const tempLabel = temperatureClassLabel(area.temperature_class)
  const { markFor, rollupByContainerId, touchedRollupByContainerId, deletedRollupByContainerId } =
    usePlantSessionPaint()
  const mark = markFor(area.id)
  const added = rollupByContainerId.get(area.id) ?? 0
  const modified = touchedRollupByContainerId.get(area.id) ?? 0
  const deleted = deletedRollupByContainerId.get(area.id) ?? 0

  return (
    <PlantHref
      id={area.id}
      label="Area"
      node={area}
      className={sessionMarkClass(
        'flex min-w-0 items-center gap-3 rounded-xl border bg-card p-3.5 text-card-foreground no-underline shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground',
        mark
      )}
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-brand-soft text-brand">
        <Icon size={20} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-semibold text-ink">
            {area.name}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {area.description && (
          <span className="block truncate text-12 text-ink-3">{area.description}</span>
        )}
        <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {kindLabel && <Badge variant="secondary">{kindLabel}</Badge>}
          {tempLabel && <Badge variant="outline">{tempLabel}</Badge>}
          {area.subAreaCount > 0 && (
            <Badge variant="outline">
              {area.subAreaCount} {area.subAreaCount === 1 ? 'sub-area' : 'sub-areas'}
            </Badge>
          )}
          {area.equipmentCount > 0 && (
            <Badge variant="outline">{area.equipmentCount} equipment</Badge>
          )}
          {area.processCount > 0 && (
            <Badge variant="outline">
              {area.processCount} {area.processCount === 1 ? 'process' : 'processes'}
            </Badge>
          )}
          {area.partCount > 0 && (
            <Badge variant="outline">
              {area.partCount} {area.partCount === 1 ? 'part' : 'parts'}
            </Badge>
          )}
          <SessionRollupPill added={added} modified={modified} deleted={deleted} />
        </span>
      </span>
    </PlantHref>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: React.ReactNode
  subtitle: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-baseline gap-3">
      <h3 className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-ink-3">{label}</h3>
      <span className="text-xs text-ink-3">{subtitle}</span>
    </div>
  )
}

const EMPTY_INDEX: PlantAreaIndex = { groups: [], strays: [] }

function totalAreaCount(index: PlantAreaIndex): number {
  let count = index.strays.length
  for (const group of index.groups) count += group.areas.length
  return count
}

function areaSubtitle(index: PlantAreaIndex, totalCount: number): string {
  if (totalCount === 0) {
    return 'Plant spatial regions — production zones, coolers, freezers, utility rooms, and more.'
  }
  let equipment = 0
  let processes = 0
  for (const group of index.groups) {
    for (const area of group.areas) {
      equipment += area.equipmentCount
      processes += area.processCount
    }
  }
  for (const area of index.strays) {
    equipment += area.equipmentCount
    processes += area.processCount
  }
  const segments = [`${totalCount} ${totalCount === 1 ? 'area' : 'areas'}`]
  if (equipment > 0) segments.push(`${equipment} equipment`)
  if (processes > 0) segments.push(`${processes} ${processes === 1 ? 'process' : 'processes'}`)
  return `${segments.join(' · ')}.`
}

export default AreasPage
